import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { isAdmin } from "auth/tier";
import type { ClientRecord, ClientStatus, ClientFormData } from "../clients.types";
import { subscribeClientsEvents, type ClientsLiveEvent } from "./clientsLive";
import { nextSort, type SortDir } from "components/TableSort/tableSort";
import { exportToCsv, type ColumnConfig } from "shared/common/utils/export";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** The registry's slug shape — the same pattern the API schema enforces (clients.schema.ts). */
const COORDINATOR_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * A Feathers query object (flattened bracket keys allowed). Local stand-in
 * for the removed transport helper's `ApiQueryParams`.
 */
type ApiQueryParams = {
  $limit?: number;
  $skip?: number;
  $sort?: Record<string, number>;
  [key: string]: unknown;
};

/** A Feathers paginated `find` envelope. */
interface PaginatedResult<T> {
  total: number;
  limit: number;
  skip: number;
  data: T[];
}

/**
 * Normalise a `find` result into a plain array of rows: a paginated
 * KnexService returns `{ data: [...] }`; a custom/derived service returns the
 * bare array. Replaces the former list-unwrap helper.
 */
function rowsOf<T>(
  result: { data?: T[] } | T[] | null | undefined,
): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

export type ClientStatusFilter = "all" | ClientStatus;

/**
 * Server-sortable columns — real `clients` DB fields only. The Health and
 * Instances rollups are computed by separate services and can never ride the
 * `$sort` of a paged `clients` find, so they stay unsortable.
 */
export type ClientSortKey = "name" | "code" | "status";

/** All three sortable columns are text — every first click sorts A→Z. */
const CLIENTS_DESC_FIRST: ReadonlySet<ClientSortKey> = new Set();

/**
 * One rendered row of the hierarchical clients table: the client plus its
 * nesting depth (0 = top-level / parent, 1 = child under its parent on the
 * same page, 2 = grandchild, …). Drives the indent + tree glyph in the Body.
 */
export interface ClientHierarchyRow {
  client: ClientRecord;
  depth: number;
}

/**
 * One row of the `client-summary` rollup service (find-only, staff-gated).
 * Contract pinned with the API builder this phase.
 */
export interface ClientSummaryRow {
  clientId: string;
  instanceCount: number;
  runningInstanceCount: number;
}

/** One row of the `client-health` rollup service. */
export interface ClientHealthRow {
  clientId: string;
  health: "green" | "yellow" | "red" | string;
  reasons: string[];
}

/** Headline counts for the summary cards (server COUNTs via `$limit: 0`). */
interface ClientCounts {
  total: number;
  active: number;
  inactive: number;
  prospect: number;
  multiCompany: number;
}

const SEARCH_DEBOUNCE_MS = 300;
export const CLIENTS_PAGE_SIZES = [10, 25, 50, 100];

// ---------------------------------------------------------------------------
// CSV export of the clients table
// ---------------------------------------------------------------------------

/** One row of the Clients CSV — the table's columns, spreadsheet-shaped. */
export interface ClientExportRow extends Record<string, unknown> {
  code: string;
  name: string;
  status: string;
  parent: string;
  health: string;
  healthReasons: string;
  runningInstances: number | "";
  totalInstances: number | "";
  industry: string;
  city: string;
  state: string;
}

/**
 * CSV shape of the clients list.
 *
 * No `format` on any column ON PURPOSE (same reasoning as the invoices export):
 * the toolkit's number formatter emits locale strings a spreadsheet re-imports
 * as text. Two differences from the rendered table, both deliberate: the
 * "running/total" instances cell becomes two numeric columns, and the
 * "City, State" cell becomes two columns — a spreadsheet can join, but it
 * cannot reliably split.
 */
export const CLIENT_EXPORT_COLUMNS: ColumnConfig[] = [
  { key: "code", label: "Code" },
  { key: "name", label: "Name" },
  { key: "status", label: "Status" },
  { key: "parent", label: "Parent" },
  { key: "health", label: "Health" },
  { key: "healthReasons", label: "Health Reasons" },
  { key: "runningInstances", label: "Running Instances" },
  { key: "totalInstances", label: "Instances" },
  { key: "industry", label: "Industry" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
];

/** The per-client rollup lookups {@link buildClientExportRows} needs. */
export interface ClientExportLookups {
  parentName: (id?: string | null) => string;
  health: (clientId: string) => ClientHealthRow | undefined;
  summary: (clientId: string) => ClientSummaryRow | undefined;
}

/**
 * Map the rendered rows to export records. PURE — no store, no download — so
 * the shaping is unit-testable on its own.
 *
 * A missing rollup exports an EMPTY cell, never a zero: the table renders "-"
 * when `client-summary`/`client-health` is unavailable, and writing 0 running
 * instances for a client nobody measured would be a fabricated number.
 */
export function buildClientExportRows(
  rows: ClientHierarchyRow[],
  lookups: ClientExportLookups
): ClientExportRow[] {
  return rows.map(({ client }) => {
    const summary = lookups.summary(client.id);
    const health = lookups.health(client.id);
    return {
      code: client.code ?? "",
      name: client.name ?? "",
      // Matches the rendered chip: an archived row reads "archived", not its
      // pre-archive lifecycle status.
      status: client.deletedAt ? "archived" : client.status || "active",
      parent: client.parentClientId ? lookups.parentName(client.parentClientId) : "",
      health: health?.health ?? "",
      healthReasons: (health?.reasons ?? []).join("; "),
      runningInstances: summary ? summary.runningInstanceCount : "",
      totalInstances: summary ? summary.instanceCount : "",
      industry: client.industry ?? "",
      city: client.city ?? "",
      state: client.state ?? "",
    };
  });
}

/**
 * View store for the Clients list.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. The `clients` service is the registered ServiceStore (see
 * serviceStores/system.stores.ts, mapped to `rootStore.clientsStore`).
 *
 * Querying is SERVER-SIDE: search (`$like` on name/code per the clients
 * querySyntax), status filter, and `$limit`/`$skip` paging all go to the API.
 * Because the list is server-paged WITH a server `total`, this store reads the
 * `find()` RESPONSE page (`data` + `total`) — the cross-query entity cache would
 * accumulate every page and lose the per-filter count — and keeps only the
 * current page locally. The parent filter remains a client-side refinement of
 * the current page (its `__none__` option needs a `parentClientId IS NULL`
 * match the query string cannot express).
 *
 * DOCUMENTED FALLBACK: if the API rejects the `$like` search query (schema not
 * yet extended — see `clientQuerySchema` in the API), the store retries the
 * find without the search predicate and filters the fetched page client-side,
 * so the search box keeps working either way. The registered ServiceStore
 * swallows find errors by default (it records `errorOnFind` and resolves null),
 * so every find here goes through `findOrThrow` — that is what lets the 400
 * reach this fallback, and what lets a failed load set `error` instead of
 * rendering as "No clients" with summary cards of 0.
 */
class ClientsStore {
  rootStore: RootStore;

  /** Current page of clients (server-paged — the `find()` response page). */
  clients: ClientRecord[] = [];
  /** Server-side total for the current filter set. */
  total = 0;
  loading = false;
  error: string | null = null;

  // Header filters
  searchQuery = "";
  statusFilter: ClientStatusFilter = "all";
  parentFilter: string | "all" = "all";
  /** Admin-only: include soft-deleted (archived) clients via includeDeleted. */
  showArchived = false;

  // Pagination
  page = 0;
  pageSize = 25;

  // Column sort — SERVER-side: it rides buildQuery's `$sort` because the list
  // is server-paged (a client-side sort would only reorder the current page).
  sortKey: ClientSortKey = "name";
  sortDir: SortDir = "asc";

  // Rollups (client-summary / client-health). Maps keyed by clientId; the
  // `*Available` flags drive graceful column hiding when a fetch fails.
  summaryByClient = new Map<string, ClientSummaryRow>();
  healthByClient = new Map<string, ClientHealthRow>();
  summaryAvailable = false;
  healthAvailable = false;

  /** Headline counts for the summary cards (independent of paging/filters). */
  counts: ClientCounts = { total: 0, active: 0, inactive: 0, prospect: 0, multiCompany: 0 };
  /**
   * Set when the headline COUNT finds failed. The cards then show "—", never a
   * zero that reads as "you have no clients".
   */
  countsError: string | null = null;

  /** Set when the API rejected `$like` search — page is then filtered locally. */
  serverSearchUnsupported = false;

  /** Restore-in-flight marker (row id) so the button can disable itself. */
  restoringId: string | null = null;
  /**
   * A failed Restore. Kept apart from `error` (which means "the LIST did not
   * load" and replaces the table with a load-error state) so a refused restore
   * never hides rows that did load.
   */
  restoreError: string | null = null;

  // Create/edit form state (shared by the ClientForm dialog)
  editingClientId: string | null = null;
  formData: ClientFormData = {};
  saving = false;
  formError: string | null = null;

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;
  private unsubscribeLive: (() => void) | null = null;
  /** Monotonic guard so a stale find can never clobber a newer page. */
  private loadSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `clients` ServiceStore (system.stores.ts). */
  private get clientsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("clients") ?? this.rootStore.clientsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /** The registered `client-summary` rollup ServiceStore (clients.stores.ts). */
  private get summaryService(): ServiceStoreType | null {
    return (this.rootStore.clientSummaryStore as ServiceStoreType | null) ?? null;
  }

  /** The registered `client-health` rollup ServiceStore (clients.stores.ts). */
  private get healthService(): ServiceStoreType | null {
    return (this.rootStore.clientHealthStore as ServiceStoreType | null) ?? null;
  }

  // ---- Lifecycle ------------------------------------------------------------

  /**
   * Route entry point (idempotent — the header Refresh button re-runs
   * `onEnter`). Loads the current page + rollups + counts and ensures the
   * realtime subscription is live.
   */
  async initialize() {
    if (!this.unsubscribeLive) {
      // Shared realtime bus (src/realtime): ONE connection for the whole app,
      // refcounted there. The JWT is sourced internally from the AuthStore —
      // there is no token argument to forget.
      this.unsubscribeLive = subscribeClientsEvents(this.rootStore, (event) =>
        this.handleLiveEvent(event),
      );
    }
    await this.reload();
  }

  /** Route exit: tear down the realtime subscription and pending debounce. */
  dispose() {
    this.unsubscribeLive?.();
    this.unsubscribeLive = null;
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }
  }

  // ---- Derived data -------------------------------------------------------

  /** admin tier only — gates the Show-archived toggle and Restore action. */
  get canShowArchived(): boolean {
    return isAdmin(this.rootStore);
  }

  get parentClients(): ClientRecord[] {
    // Clients on the current page referenced as a parent by at least one
    // other client on the page (page-local by design under server paging).
    const parentIds = new Set(
      this.clients
        .map((c) => c.parentClientId)
        .filter((id): id is string => !!id)
    );
    return this.clients.filter((c) => parentIds.has(c.id));
  }

  clientName(id?: string | null): string {
    if (!id) return "";
    return this.clients.find((c) => c.id === id)?.name || "";
  }

  /**
   * Rows to render: the server-filtered page, refined by the page-local parent
   * filter and (only in the documented fallback) the client-side search.
   */
  get filteredClients(): ClientRecord[] {
    let result = this.clients;

    if (this.parentFilter !== "all") {
      if (this.parentFilter === "__none__") {
        result = result.filter((c) => !c.parentClientId);
      } else {
        result = result.filter((c) => c.parentClientId === this.parentFilter);
      }
    }

    if (this.serverSearchUnsupported && this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q)
      );
    }

    return result;
  }

  /**
   * The rendered rows in parent → child order: each top-level client is
   * immediately followed by its children (and their children) that are present
   * on the current page, depth-tagged for indentation. A client whose parent is
   * NOT on the page renders at depth 0 so it is never hidden (server paging /
   * filters can separate a child from its parent). Cycle-guarded. Input order
   * (the server sort, refined by {@link filteredClients}) is preserved within
   * each level.
   *
   * SORT vs HIERARCHY: grouping children under their parent only reads
   * sensibly when the server ordering is by name — under a code or status
   * sort, regrouping would scramble the ordering the user just asked for, so
   * the rows render sorted-flat (all depth 0) until the name sort is active
   * again.
   */
  get hierarchicalClients(): ClientHierarchyRow[] {
    const rows = this.filteredClients;
    if (this.sortKey !== "name") {
      return rows.map((client) => ({ client, depth: 0 }));
    }
    const byId = new Map(rows.map((c) => [c.id, c]));
    const childrenByParent = new Map<string, ClientRecord[]>();
    const roots: ClientRecord[] = [];
    for (const c of rows) {
      const pid = c.parentClientId;
      if (pid && pid !== c.id && byId.has(pid)) {
        const list = childrenByParent.get(pid) ?? [];
        list.push(c);
        childrenByParent.set(pid, list);
      } else {
        roots.push(c);
      }
    }
    const out: ClientHierarchyRow[] = [];
    const seen = new Set<string>();
    const visit = (c: ClientRecord, depth: number) => {
      if (seen.has(c.id)) return; // cycle / double-parent guard
      seen.add(c.id);
      out.push({ client: c, depth });
      for (const kid of childrenByParent.get(c.id) ?? []) visit(kid, depth + 1);
    };
    for (const r of roots) visit(r, 0);
    // Safety net: surface any row not reached (e.g. a child caught in a cycle)
    // so the table can never silently drop a client.
    for (const c of rows) if (!seen.has(c.id)) out.push({ client: c, depth: 0 });
    return out;
  }

  get totalCount(): number {
    return this.counts.total;
  }

  /**
   * True when the summary-card numbers are not known: the list itself failed
   * (counts are only fetched after a successful page) or the COUNT finds did.
   * The cards then show "—" with no success colour.
   */
  get countsUnavailable(): boolean {
    return this.error !== null || this.countsError !== null;
  }

  /**
   * True when a server-side or page-local narrowing is in effect, so an empty
   * table may honestly say "No clients match the current filters". Otherwise a
   * successful zero-row load says "No clients yet".
   */
  get hasActiveFilters(): boolean {
    return (
      this.searchQuery.trim() !== "" ||
      this.statusFilter !== "all" ||
      this.parentFilter !== "all"
    );
  }

  get activeCount(): number {
    return this.counts.active;
  }

  get inactiveCount(): number {
    return this.counts.inactive;
  }

  get prospectCount(): number {
    return this.counts.prospect;
  }

  get multiCompanyCount(): number {
    return this.counts.multiCompany;
  }

  summaryFor(clientId: string): ClientSummaryRow | undefined {
    return this.summaryByClient.get(clientId);
  }

  healthFor(clientId: string): ClientHealthRow | undefined {
    return this.healthByClient.get(clientId);
  }

  /**
   * Honest caption for the PAGE-LOCAL refinements.
   *
   * The pager's count and the summary cards are server numbers (a real
   * `$limit: 0` COUNT / the server `total`), but the Parent filter — and the
   * documented client-side search fallback — narrow only the rows already
   * fetched. When one of those is active the table shows fewer rows than the
   * pager claims to be paging through, so say which number is which instead of
   * letting the discrepancy read as missing data.
   */
  get pageFilterNotice(): string | null {
    const shown = this.filteredClients.length;
    const loaded = this.clients.length;
    if (shown === loaded) return null;
    const reasons: string[] = [];
    if (this.parentFilter !== "all") reasons.push("Parent filter");
    if (this.serverSearchUnsupported && this.searchQuery.trim()) {
      reasons.push("search");
    }
    const by = reasons.length > 0 ? ` (${reasons.join(" + ")})` : "";
    return (
      `Showing ${shown} of the ${loaded} clients on this page${by} — that filter ` +
      `narrows the loaded page only. The pager and the cards above count all ` +
      `${this.total} clients matching the server-side filters.`
    );
  }

  // ---- CSV export ---------------------------------------------------------

  /**
   * What "Export CSV" writes: the rows currently ON SCREEN, in screen order —
   * the server page (search + status + archived + sort) refined by the
   * page-local Parent filter, in the hierarchical parent→child order the table
   * renders. This is a PAGE, not the whole result set: the list is server-paged
   * and exporting a hidden set would silently disagree with what was exported
   * from.
   */
  get exportRows(): ClientExportRow[] {
    return buildClientExportRows(this.hierarchicalClients, {
      parentName: (id) => this.clientName(id),
      health: (id) => this.healthFor(id),
      summary: (id) => this.summaryFor(id),
    });
  }

  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  /**
   * Download the visible page as CSV. Returns false instead of throwing when
   * there is nothing to export (the toolkit throws on an empty set) so the
   * header can stay a plain onClick.
   */
  exportCsv(): boolean {
    const rows = this.exportRows;
    if (rows.length === 0) return false;
    try {
      exportToCsv(rows, CLIENT_EXPORT_COLUMNS, {
        fileName: `clients-${new Date().toISOString().slice(0, 10)}`,
      });
      return true;
    } catch (err) {
      console.error("clients: CSV export failed", err);
      return false;
    }
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
    // Debounced server requery; the page resets because the result set changed.
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      runInAction(() => {
        this.page = 0;
      });
      void this.loadClients();
    }, SEARCH_DEBOUNCE_MS);
  }

  setStatusFilter(filter: ClientStatusFilter) {
    this.statusFilter = filter;
    this.page = 0;
    void this.loadClients();
  }

  setParentFilter(filter: string | "all") {
    // Page-local refinement — no server round-trip needed.
    this.parentFilter = filter;
  }

  toggleShowArchived() {
    if (!this.canShowArchived) return;
    this.showArchived = !this.showArchived;
    this.page = 0;
    void this.loadClients();
  }

  setPage(page: number) {
    this.page = page;
    void this.loadClients();
  }

  setPageSize(size: number) {
    this.pageSize = size;
    this.page = 0;
    void this.loadClients();
  }

  /**
   * Column-header click: same column toggles direction, a new column starts
   * A→Z (all sortable columns are text). The ordering itself happens on the
   * SERVER via buildQuery's `$sort`, so the page resets and reloads — page N
   * of the old ordering is meaningless under the new one.
   */
  setSort(key: ClientSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, CLIENTS_DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
    this.page = 0;
    void this.loadClients();
  }

  // ---- Loading ------------------------------------------------------------

  /**
   * Server query for the current page. Flattened bracket keys (e.g.
   * `$or[0][name][$like]`) are used because the shared `buildQueryString`
   * helper only serialises one level of nesting.
   */
  private buildQuery(includeSearch: boolean): ApiQueryParams {
    const query: ApiQueryParams = {
      $limit: this.pageSize,
      $skip: this.page * this.pageSize,
      $sort: { [this.sortKey]: this.sortDir === "asc" ? 1 : -1 },
    };
    if (this.statusFilter !== "all") {
      query.status = this.statusFilter;
    }
    if (this.showArchived && this.canShowArchived) {
      query.includeDeleted = true;
    }
    const q = this.searchQuery.trim();
    if (includeSearch && q) {
      const pattern = `%${q}%`;
      query["$or[0][name][$like]"] = pattern;
      query["$or[1][code][$like]"] = pattern;
    }
    return query;
  }

  async loadClients() {
    const seq = ++this.loadSeq;
    this.loading = true;
    this.error = null;
    const q = this.searchQuery.trim();
    try {
      let result: PaginatedResult<ClientRecord> | null;
      try {
        result = await findOrThrow<PaginatedResult<ClientRecord> | null>(this.clientsService, {
          query: this.buildQuery(true),
        });
        if (q) {
          runInAction(() => {
            this.serverSearchUnsupported = false;
          });
        }
      } catch (err: unknown) {
        // Ended by a sign-out: the outer catch says nothing for it; no retry.
        if (isSessionEnded(err)) throw err;
        // DOCUMENTED FALLBACK: a 400 on a search query means the API's
        // clientQuerySchema does not (yet) allow `$like`. Retry without the
        // search predicate and filter the page client-side instead.
        const status = (err as { status?: number })?.status;
        if (q && status === 400) {
          runInAction(() => {
            this.serverSearchUnsupported = true;
          });
          result = await findOrThrow<PaginatedResult<ClientRecord> | null>(this.clientsService, {
            query: this.buildQuery(false),
          });
        } else {
          throw err;
        }
      }
      if (seq !== this.loadSeq) return; // a newer load superseded this one
      const page = result?.data ?? [];
      runInAction(() => {
        this.clients = page;
        this.total = typeof result?.total === "number" ? result.total : page.length;
      });
      // Rollups + headline counts ride along but never block (or fail) the grid.
      void this.loadRollups();
      void this.loadCounts();
    } catch (err: unknown) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Failed to load clients");
        // A failed load has no rows — never leave the previous page on screen
        // under an error, and never let it read as "no clients".
        this.clients = [];
        this.total = 0;
      });

      console.error("Failed to load clients:", err);
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /**
   * Batch-fetch the per-client rollups: ONE find against `client-summary` and
   * ONE against `client-health`, mapped by clientId. Each degrades gracefully
   * and independently — a failed fetch just hides its columns. INTENTIONALLY
   * TOLERANT (no page error): the rollups are decoration on the client list.
   * They still go through `findOrThrow`, because a swallowed failure used to
   * resolve null, flip `*Available` to TRUE and paint every row "-".
   */
  async loadRollups() {
    // No query params: both services validate additionalProperties:false with
    // ONLY an optional clientId — $limit would 400. Unfiltered finds return a
    // row for every non-deleted client. These find-only rollups are re-queried
    // per load, so read the `find()` RESPONSE rows (not the entity cache).
    const ended = sessionMark(this.rootStore);
    const [summaries, healths] = await Promise.allSettled([
      findOrThrow(this.summaryService, { query: {} }),
      findOrThrow(this.healthService, { query: {} }),
    ]);
    // Signed out meanwhile: the rows are the previous person's, and a rejection
    // is the guard's, not an unavailable rollup.
    if (ended()) return;

    runInAction(() => {
      if (summaries.status === "fulfilled") {
        const map = new Map<string, ClientSummaryRow>();
        for (const row of rowsOf<ClientSummaryRow>(summaries.value as unknown as ClientSummaryRow[])) {
          if (row?.clientId) map.set(row.clientId, row);
        }
        this.summaryByClient = map;
        this.summaryAvailable = true;
      } else {
        this.summaryAvailable = false;

        console.warn("client-summary rollup unavailable:", summaries.reason);
      }

      if (healths.status === "fulfilled") {
        const map = new Map<string, ClientHealthRow>();
        for (const row of rowsOf<ClientHealthRow>(healths.value as unknown as ClientHealthRow[])) {
          if (row?.clientId) map.set(row.clientId, row);
        }
        this.healthByClient = map;
        this.healthAvailable = true;
      } else {
        this.healthAvailable = false;

        console.warn("client-health rollup unavailable:", healths.reason);
      }
    });
  }

  /**
   * Headline counts for the summary cards. Cheap server COUNTs (`$limit: 0`
   * returns only `total`), independent of the page/filters so the cards keep
   * showing the global picture. A failure sets `countsError` so the cards
   * render "—" rather than a zero (see {@link countsUnavailable}).
   */
  async loadCounts() {
    const ended = sessionMark(this.rootStore);
    try {
      const count = async (extra: ApiQueryParams = {}) => {
        const res = await findOrThrow<PaginatedResult<ClientRecord> | null>(this.clientsService, {
          query: { $limit: 0, ...extra },
        });
        return typeof res?.total === "number" ? res.total : 0;
      };
      const [total, active, inactive, prospect, multiCompany] = await Promise.all([
        count(),
        count({ status: "active" }),
        count({ status: "inactive" }),
        count({ status: "prospect" }),
        count({ isMultiCompany: true }),
      ]);
      if (ended()) return;
      runInAction(() => {
        this.counts = { total, active, inactive, prospect, multiCompany };
        this.countsError = null;
      });
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        this.countsError = loadErrorMessage(err, "Failed to load client counts");
      });

      console.warn("Failed to load client counts:", err);
    }
  }

  /** Alias used by header refresh / post-write callbacks. */
  async reload() {
    await this.loadClients();
    // Keep the global ClientStore (selector dropdown) in sync after a write.
    const clientStore = (this.rootStore as Record<string, unknown>).clientStore as
      | { fetchClients?: () => Promise<void> }
      | undefined;
    clientStore?.fetchClients?.();
  }

  // ---- Realtime -----------------------------------------------------------

  /**
   * Apply a live `clients` CRUD event to the grid IN PLACE — no full reload.
   * The headline counts are re-pulled (cheap `$limit: 0` COUNTs) since any
   * mutation can shift them.
   */
  handleLiveEvent(event: ClientsLiveEvent) {
    const { action, record } = event;
    runInAction(() => {
      const idx = this.clients.findIndex((c) => c.id === record.id);

      if (action === "removed") {
        // Hard remove (or a soft delete surfaced as `removed`): drop the row
        // unless archived rows are being shown, in which case keep it muted.
        if (idx >= 0) {
          if (this.showArchived) {
            this.clients[idx] = { ...this.clients[idx], ...record };
          } else {
            this.clients.splice(idx, 1);
            this.total = Math.max(0, this.total - 1);
          }
        }
      } else if (action === "created") {
        // Insert in place when the row matches the current status filter and
        // no narrower search is active; otherwise just bump the total.
        const matchesStatus =
          this.statusFilter === "all" || record.status === this.statusFilter;
        if (idx < 0 && matchesStatus && !this.searchQuery.trim()) {
          this.clients = [...this.clients, record].sort((a, b) =>
            (a.name || "").localeCompare(b.name || "")
          );
        }
        this.total += idx < 0 ? 1 : 0;
      } else {
        // patched / updated
        if (idx >= 0) {
          const merged = { ...this.clients[idx], ...record };
          if (merged.deletedAt && !this.showArchived) {
            // Archived out from under the current view.
            this.clients.splice(idx, 1);
            this.total = Math.max(0, this.total - 1);
          } else {
            this.clients[idx] = merged;
          }
        }
      }
    });
    void this.loadCounts();
  }

  // ---- Archive / restore --------------------------------------------------

  /** Restore an archived client (admin-only UI) by clearing `deletedAt`. */
  async restoreClient(client: ClientRecord): Promise<void> {
    if (!this.canShowArchived || this.restoringId) return;
    this.restoringId = client.id;
    this.restoreError = null;
    try {
      // Ask the store to throw, as saveClient does: by default a refused patch
      // resolves null and the row would flip to "restored" when it was not.
      const restored = (await this.clientsService?.patch(
        client.id,
        { deletedAt: null },
        { throwOnError: true } as never
      )) as unknown as Partial<ClientRecord> | undefined;
      runInAction(() => {
        const idx = this.clients.findIndex((c) => c.id === client.id);
        if (idx >= 0)
          this.clients[idx] = {
            ...this.clients[idx],
            ...(restored ?? {}),
            deletedAt: null,
          };
      });
      void this.loadCounts();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.restoreError = loadErrorMessage(err, "Failed to restore client");
      });

      console.error("restoreClient failed:", err);
    } finally {
      runInAction(() => {
        this.restoringId = null;
      });
    }
  }

  // ---- Create / edit form -------------------------------------------------

  startCreate() {
    this.editingClientId = null;
    this.formError = null;
    this.formData = {
      name: "",
      code: "",
      type: "standard",
      status: "active",
      defaultCurrency: "USD",
      fiscalYearEnd: "12-31",
      isMultiCompany: false,
      parentClientId: null,
      industry: "",
    };
  }

  startEdit(client: ClientRecord) {
    this.editingClientId = client.id;
    this.formError = null;
    this.formData = {
      name: client.name,
      code: client.code,
      type: client.type || "standard",
      status: client.status || "active",
      industry: client.industry || "",
      parentClientId: client.parentClientId || null,
      isMultiCompany: !!client.isMultiCompany,
      coordinatorSlug: client.coordinatorSlug || "",
      developerUserId: client.developerUserId ?? null,
      address: client.address || "",
      city: client.city || "",
      state: client.state || "",
      zip: client.zip || "",
      phone: client.phone || "",
      email: client.email || "",
      website: client.website || "",
      taxId: client.taxId || "",
      defaultCurrency: client.defaultCurrency || "USD",
      fiscalYearEnd: client.fiscalYearEnd || "12-31",
      notes: client.notes || "",
    };
    // [hub] review round 1 (Antigravity #7): load the staff list now, not when
    // the picker is first opened — until then the stored developer matched no
    // option and the field rendered blank. (Admin only; a no-op otherwise.)
    void this.loadDeveloperOptions();
  }

  get isEditing(): boolean {
    return !!this.editingClientId;
  }

  /**
   * [hub] H1 — only an admin may set a client's developer (the API refuses
   * anyone else with 403 `admin-only`), so only an admin is offered the field.
   */
  get canSetDeveloper(): boolean {
    return isAdmin(this.rootStore);
  }

  /** Staff a developer can be chosen from (the builtin `userStore` cache). */
  get developerOptions(): Array<{ id: number; email: string; label: string }> {
    const users = ((this.rootStore.userStore as unknown as ServiceStoreType | null)?.entities ?? []) as unknown as Array<{
      id: number;
      email?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      isValidated?: boolean | number | null;
    }>;
    return users
      .filter((u) => typeof u.id === "number" && !!u.email && (u.isValidated === undefined || !!u.isValidated))
      .map((u) => {
        const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
        return { id: u.id, email: String(u.email), label: name ? `${name} (${u.email})` : String(u.email) };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private developersLoaded = false;

  /** Load the staff list behind the developer picker, once. Admin only. */
  async loadDeveloperOptions() {
    if (this.developersLoaded || !this.canSetDeveloper) return;
    const ended = sessionMark(this.rootStore);
    try {
      // findOrThrow: the library store swallows a failed find (and the session
      // guard's rejection), and a swallowed one used to latch the picker empty.
      await findOrThrow(this.rootStore.userStore as unknown as Parameters<typeof findOrThrow>[0], {
        query: { $limit: 500 },
      });
      if (ended()) return;
      runInAction(() => {
        this.developersLoaded = true;
      });
    } catch (err: unknown) {
      if (ended()) return;
      console.error("Failed to load staff for the developer picker:", err);
    }
  }

  setFormField(field: keyof ClientFormData, value: string | boolean | number | null) {
    this.formData = { ...this.formData, [field]: value } as ClientFormData;
  }

  /** Why the coordinator slug cannot be saved, or null. The API enforces the same shape. */
  get coordinatorSlugError(): string | null {
    const slug = this.formData.coordinatorSlug?.trim();
    if (!slug) return null;
    return COORDINATOR_SLUG.test(slug)
      ? null
      : "Lower-case letters, digits and single hyphens only, as the registry writes it (e.g. premier).";
  }

  get isFormValid(): boolean {
    return (
      !!this.formData.name?.trim() &&
      !!this.formData.code?.trim() &&
      this.coordinatorSlugError === null
    );
  }

  /**
   * Persist the create/edit form. Code uniqueness is enforced by the API
   * (the `clients.code` unique constraint); any duplicate surfaces as a
   * Feathers validation error captured in `formError`.
   */
  async saveClient(): Promise<boolean> {
    if (!this.isFormValid) {
      this.formError = this.coordinatorSlugError ?? "Name and Code are required.";
      return false;
    }
    this.saving = true;
    this.formError = null;
    try {
      // Build a clean payload — omit empty strings for nullable fields and
      // normalise the code to uppercase to match existing client conventions.
      const payload: Record<string, unknown> = {
        name: this.formData.name?.trim(),
        code: this.formData.code?.trim().toUpperCase(),
        type: this.formData.type || "standard",
        status: this.formData.status || "active",
        isMultiCompany: !!this.formData.isMultiCompany,
        parentClientId: this.formData.parentClientId || null,
      };
      const optional: Array<keyof ClientFormData> = [
        "industry",
        "address",
        "city",
        "state",
        "zip",
        "phone",
        "email",
        "website",
        "taxId",
        "fiscalYearEnd",
        "defaultCurrency",
        "notes",
      ];
      for (const key of optional) {
        const v = this.formData[key];
        if (v != null && v !== "") payload[key] = v;
      }
      // Unlike the fields above, an emptied slug must reach the API as null:
      // moving a slug to another client means clearing it here first.
      const slug = this.formData.coordinatorSlug?.trim();
      if (slug) payload.coordinatorSlug = slug;
      else if (this.editingClientId) payload.coordinatorSlug = null;
      // [hub] H1: only an admin sends it — anyone else's save would be refused whole.
      if (this.canSetDeveloper && this.formData.developerUserId !== undefined) {
        payload.developerUserId = this.formData.developerUserId ?? null;
      }

      // Per call, not store-wide: 13 views read `clients`, and most treat a
      // null result as the failure. This one catches, so it asks the store to
      // throw — otherwise a refused save (a duplicate coordinator slug, a
      // taken code) resolved null and the form closed as if it had saved.
      const throwing = { throwOnError: true } as never;
      if (this.editingClientId) {
        await this.clientsService?.patch(this.editingClientId, payload, throwing);
      } else {
        await this.clientsService?.create(payload as never, throwing);
      }

      await this.reload();
      runInAction(() => {
        this.editingClientId = null;
        this.formData = {};
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.formError = err instanceof Error ? err.message : "Failed to save client";
      });

      console.error("saveClient failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.clients = [];
    this.total = 0;
    this.loading = false;
    this.error = null;
    this.searchQuery = "";
    this.statusFilter = "all";
    this.parentFilter = "all";
    this.showArchived = false;
    this.page = 0;
    this.pageSize = 25;
    this.sortKey = "name";
    this.sortDir = "asc";
    this.summaryByClient = new Map<string, ClientSummaryRow>();
    this.healthByClient = new Map<string, ClientHealthRow>();
    this.summaryAvailable = false;
    this.healthAvailable = false;
    this.counts = { total: 0, active: 0, inactive: 0, prospect: 0, multiCompany: 0 };
    this.countsError = null;
    this.serverSearchUnsupported = false;
    this.restoringId = null;
    this.restoreError = null;
    this.editingClientId = null;
    this.formData = {};
    this.saving = false;
    this.formError = null;
    this.searchDebounce = null;
    this.unsubscribeLive = null;
    this.loadSeq += 1;
    this.developersLoaded = false;
  }
}

export default ClientsStore;
